import React, { useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { HStack, VStack } from '@musinsa/mwms-ds'
import { colors, colorSystem, fontSystem } from '@/styles/theme'
import { tokens } from '@musinsa/layer-tokens/desktop'

export interface TabProps<T> {
  key: T
  label: React.ReactNode
  content: React.ReactNode
}

export interface TabListProps<T> {
  className?: string
  radius?: string
  list: TabProps<T>[]
  defaultKey?: T
  activeKey?: T
  onTabChange?: (key: T) => void
}

function TabList<T extends string>({ className, radius, list, defaultKey, activeKey: injectedActiveKey, onTabChange }: TabListProps<T>) {
  const [localActiveKey, setLocalActiveKey] = useState<T>(defaultKey || list?.[0].key)

  const listMap: Record<T, TabProps<T>> = useMemo(
    () =>
      list.reduce(
        (acc, item) => {
          acc[item.key] = item
          return acc
        },
        {} as Record<T, TabProps<T>>,
      ),
    [list],
  )
  const activeKey = useMemo<T>(() => injectedActiveKey || localActiveKey, [injectedActiveKey, localActiveKey])

  return (
    <>
      {list.length ? (
        <TabListWrap className={className}>
          <TabItemList $radius={radius} className="tabs">
            {list.map((item) => {
              const active = activeKey === item.key

              return (
                <TabItem
                  className="tabs-item"
                  $active={active}
                  onClick={() => {
                    if (!injectedActiveKey) {
                      setLocalActiveKey(item.key)
                      return
                    }

                    if (onTabChange) {
                      onTabChange(item.key)
                    }
                  }}
                  key={`tab-item-${item.key}`}
                  role="button"
                >
                  {item.label}
                </TabItem>
              )
            })}
          </TabItemList>
          {listMap[activeKey] && <TabContent>{listMap[activeKey].content}</TabContent>}
        </TabListWrap>
      ) : null}
    </>
  )
}

const TabListWrap = styled(VStack)`
  height: 100%;
`

const TabItemList = styled(HStack)<{ $radius?: string }>`
  padding-top: 8px;
  border-radius: ${({ $radius }) => $radius || '8px 8px 0 0'};
  border: 1px solid ${colorSystem.border.table};
  border-bottom: 0;
  background: #fff;
`

const TabItem = styled.div<{ $active: boolean }>`
  padding: 8px 12px;
  border-bottom: 2px solid transparent;
  color: ${colors.gray500};
  ${fontSystem.Headline_16_regular};

  ${({ $active }) =>
    $active &&
    css`
      border-color: ${tokens.layerDesktop['fg-accent']};
      color: ${colors.gray800};
      font-weight: 700;
    `}
`

const TabContent = styled.div`
  height: 100%;
`

export default TabList
